import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface MonofasicoAnpListagem {
  id: number;
  anpId: number;
  codigoAnp?: string | null;
  descricaoAnp?: string | null;
  adRemIcms: number;
  bio: boolean;
  origemNacional: boolean;
  descricaoMedida?: string | null;
}

export interface MonofasicoAnpDetalhe extends MonofasicoAnpListagem {
  unidadeMedidaId?: number | null;
  observacoes?: string | null;
}

export interface SalvarMonofasicoAnpRequest {
  anpId: number;
  codigoAnp?: string | null;
  descricaoAnp?: string | null;
  bio: boolean;
  origemNacional: boolean;
  adRemIcms: number;
  unidadeMedidaId?: number | null;
  descricaoMedida?: string | null;
  observacoes?: string | null;
}

interface Paginado<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

const BASE = "/api/cadastros/fiscal/monofasico-anp";

export function useMonofasicoAnp(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["monofasico-anp"] });

  const query = useQuery({
    queryKey: ["monofasico-anp", busca, pagina, tamanhoPagina],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginado<MonofasicoAnpListagem>>(BASE, {
        params: { busca: busca || undefined, pagina, tamanhoPagina },
      });
      return data;
    },
  });

  const obterPorId = async (id: number) =>
    (await apiClient.get<MonofasicoAnpDetalhe>(`${BASE}/${id}`)).data;

  const criar = useMutation({
    mutationFn: async (req: SalvarMonofasicoAnpRequest) => (await apiClient.post(BASE, req)).data,
    onSuccess: () => { inv(); toast.success("Monofásico ANP criado."); },
    onError: (e: Error) => toast.error("Erro ao criar: " + e.message),
  });

  const atualizar = useMutation({
    mutationFn: async ({ id, req }: { id: number; req: SalvarMonofasicoAnpRequest }) => {
      await apiClient.put(`${BASE}/${id}`, req);
    },
    onSuccess: () => { inv(); toast.success("Monofásico ANP atualizado."); },
    onError: (e: Error) => toast.error("Erro ao atualizar: " + e.message),
  });

  const excluir = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${BASE}/${id}`); },
    onSuccess: () => { inv(); toast.success("Monofásico ANP excluído."); },
    onError: (e: Error) => toast.error("Erro ao excluir: " + e.message),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}
